import React, { useEffect, useRef, useState } from 'react';
import {
  Calendar, Users, Activity, FileText, Brain, Shield,
  Zap, BarChart3, MessageSquare, ArrowRight,
  CheckCircle2, Cpu, Globe, LogIn, UserPlus, Plus, Minus,
  Star, Check, TrendingUp, Lock, Stethoscope, Rocket,
  CreditCard, Pill, Building2, Heart, Plug, Phone,
  Camera, ClipboardList, Fingerprint, Receipt, GraduationCap, Sparkles,
} from 'lucide-react';

/**
 * ORALZY — Futuristic marketing landing page.
 *
 * Props:
 *   onGetStarted — called when user clicks any "Sign up" / "Get started" CTA
 *   onSignIn     — called when user clicks any "Log in" / "Sign in" CTA
 */

/* ─────────── Brand palette (from logo) ─────────── */
const OZ = {
  orange:      '#F57C1F',
  orangeDark:  '#DC6A0F',
  orangeLight: '#FFA559',
  blue:        '#29A8E0',
  blueDark:    '#1F8CBC',
  blueLight:   '#7DC9EA',
};

/* ─────────── Oralzy Logo (inline SVG — crisp at any size) ─────────── */
export const OralzyLogo = ({ height = 28, onDark = false }) => {
  /* Geometry — tuned so dot sits at the start of "O" and arc extends
     the same distance past "y" on the right (symmetric about the wordmark). */
  const VB_W = 280;                   // viewBox width
  const TEXT_Y = 62;
  const FS = 68;
  const ORA_X = 18;                   // left edge of "O"
  const LZY_X = ORA_X + 120;          // left edge of "l" (right after "a")
  const DOT_X = ORA_X - 2;            // dot slightly before left edge of "O"
  const ARC_END_X = VB_W - DOT_X;     // symmetric endpoint past "y"

  return (
    <svg
      viewBox={`0 0 ${VB_W} 96`}
      height={height}
      style={{ display: 'block' }}
      aria-label="Oralzy"
    >
      <defs>
        <linearGradient id="oz-arc" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={OZ.orange} />
          <stop offset="100%" stopColor={OZ.blue} />
        </linearGradient>
      </defs>
      {/* "Ora" in orange */}
      <text x={ORA_X} y={TEXT_Y}
        fontFamily="'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
        fontWeight="900" fontSize={FS} fill={OZ.orange} letterSpacing="-1.5">Ora</text>
      {/* "lzy" in blue */}
      <text x={LZY_X} y={TEXT_Y}
        fontFamily="'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
        fontWeight="900" fontSize={FS} fill={OZ.blue} letterSpacing="-1.5">lzy</text>
      {/* Orange dot — at the start of "O" */}
      <circle cx={DOT_X} cy="82" r="5.5" fill={OZ.orange} />
      {/* Arc — symmetric: starts at dot under "O", ends past "y" by the same offset */}
      <path
        d={`M ${DOT_X} 82 Q ${VB_W / 2} 112 ${ARC_END_X} 72`}
        stroke="url(#oz-arc)"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
      {/* subtle white stroke on dark bg for legibility */}
      {onDark && (
        <>
          <text x={ORA_X} y={TEXT_Y}
            fontFamily="'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
            fontWeight="900" fontSize={FS} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" letterSpacing="-1.5">Ora</text>
          <text x={LZY_X} y={TEXT_Y}
            fontFamily="'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
            fontWeight="900" fontSize={FS} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" letterSpacing="-1.5">lzy</text>
        </>
      )}
    </svg>
  );
};

/* Compact "O" mark — used where only the icon fits (favicons, tiny nav). */
export const OralzyMark = ({ size = 36 }) => (
  <svg viewBox="0 0 40 40" width={size} height={size} aria-label="Oralzy">
    <defs>
      <linearGradient id="oz-mark-grad" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={OZ.orange} />
        <stop offset="100%" stopColor={OZ.blue} />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="40" height="40" rx="11" fill="url(#oz-mark-grad)" />
    <text x="50%" y="54%" textAnchor="middle" dominantBaseline="middle"
      fontFamily="'Inter', system-ui, -apple-system, sans-serif"
      fontWeight="900" fontSize="26" fill="#fff" letterSpacing="-0.5">O</text>
  </svg>
);

const ROTATING_WORDS = ['operating system', 'command center', 'AI co-pilot', 'growth engine', 'nerve center'];

/* ─────────── Small helpers ─────────── */

const useReveal = () => {
  const refs = useRef([]);
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('oz-revealed')),
      { threshold: 0.15 }
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);
  return (el) => { if (el && !refs.current.includes(el)) refs.current.push(el); };
};

/** Animated counter — counts up when scrolled into view. */
const Counter = ({ to, suffix = '', prefix = '', duration = 1600 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(to * eased);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);

  const display = Number.isInteger(to) ? Math.round(val).toLocaleString() : val.toFixed(1);
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
};

/** Accordion item for FAQ. */
const FAQItem = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      borderBottom: '1px solid rgba(255,255,255,0.07)',
      padding: '20px 4px',
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'transparent', border: 'none', color: '#fff',
          fontSize: 17, fontWeight: 600, textAlign: 'left', cursor: 'pointer', padding: 0,
        }}
      >
        {q}
        <span style={{
          width: 32, height: 32, borderRadius: '50%',
          background: open ? 'rgba(245,124,31,0.18)' : 'rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.25s', flexShrink: 0, marginLeft: 16,
        }}>
          {open ? <Minus size={15} color="#7DC9EA" /> : <Plus size={15} color="#94a3b8" />}
        </span>
      </button>
      <div style={{
        maxHeight: open ? 200 : 0, overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(.2,.8,.2,1), opacity 0.3s',
        opacity: open ? 1 : 0, marginTop: open ? 12 : 0,
      }}>
        <div style={{ color: '#94a3b8', fontSize: 15, lineHeight: 1.65 }}>{a}</div>
      </div>
    </div>
  );
};

/* ─────────── Main Component ─────────── */

const LandingPage = ({ onGetStarted, onSignIn }) => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [wordIdx, setWordIdx] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [scrollPct, setScrollPct] = useState(0);
  const [footerPage, setFooterPage] = useState(null);
  const addReveal = useReveal();

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY);
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setScrollPct(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    const onMouse = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setWordIdx((i) => (i + 1) % ROTATING_WORDS.length), 2600);
    return () => clearInterval(id);
  }, []);

  const pX = (mousePos.x / (window.innerWidth || 1) - 0.5) * 40;
  const pY = (mousePos.y / (window.innerHeight || 1) - 0.5) * 40;

  /* ─── Data ─── */

  const bento = [
    {
      icon: Brain, tint: '#F57C1F', span: 2,
      title: 'Veeransh AI', eyebrow: 'AI Co-pilot',
      desc: 'Voice-dictate notes. Summarise visits. Draft prescriptions. Built for dentistry.',
      visual: 'ai',
    },
    {
      icon: Calendar, tint: '#29A8E0', span: 1,
      title: 'Smart Scheduling', eyebrow: 'Operations',
      desc: 'Fill chairs, eliminate gaps, auto-recall.',
      visual: 'calendar',
    },
    {
      icon: BarChart3, tint: '#f59e0b', span: 1,
      title: 'Live Analytics', eyebrow: 'Insights',
      desc: 'Revenue forecasts and growth in real time.',
      visual: 'chart',
    },
    {
      icon: MessageSquare, tint: '#10b981', span: 1,
      title: 'WhatsApp Native', eyebrow: 'Engage',
      desc: 'Recalls, reminders, campaigns — one click.',
      visual: 'chat',
    },
    {
      icon: Shield, tint: '#3b82f6', span: 1,
      title: 'NABH Ready', eyebrow: 'Compliance',
      desc: 'SOPs, registers and audit trails built-in.',
      visual: 'shield',
    },
    {
      icon: Users, tint: '#ec4899', span: 2,
      title: 'Patient 360°', eyebrow: 'Records',
      desc: 'Every X-ray, Rx, payment and chat — woven into one unified timeline.',
      visual: 'patient',
    },
  ];

  /* ─── Feature Universe — every module in the software, grouped into 12
     domains. Drives the "Every module, one platform" section. Each card
     shows the icon + top items and a total count footer. ───────────────── */
  const featureUniverse = [
    {
      icon: ClipboardList, tint: OZ.orange,
      title: 'Clinical Records',
      blurb: 'Every piece of a patient\'s story, chronologically stitched.',
      items: [
        'Patient 360° profile with visits, notes, bills, consents',
        'Interactive dental chart / odontogram (FDI + Universal)',
        'Periodontal charting — pocket depths, recession, BOP',
        'Radiograph & DICOM viewer with AI anomaly detection',
        'DSLR photo capture + before/after comparison',
        'Clinical SOAP notes & chief-complaint templates',
        'Prescription builder with drug-interaction checks',
        'Treatment plans — multi-visit, cost estimates, patient e-sign',
        'Treatment outcome tracking & success rates',
        'Ortho / Implant / Perio case modules',
      ],
    },
    {
      icon: Calendar, tint: OZ.blue,
      title: 'Appointments & Scheduling',
      blurb: 'Fill every chair without overbooking.',
      items: [
        'Day / week / month calendar with drag-drop reschedule',
        'Smart scheduling AI — gap filling + slot recommendation',
        'Waitlist auto-promotion when slots open',
        'Patient recall engine — treatment-based + periodic',
        'Online booking widget for your website',
        'Check-in queue with live wait-time display',
        'Telemedicine (Jitsi) with auto-generated rooms',
        'Double-book prevention with atomic slot locking',
        'SMS / WhatsApp / email reminders',
      ],
    },
    {
      icon: Receipt, tint: '#10b981',
      title: 'Billing & Finance',
      blurb: 'GST-compliant invoices, payments and reconciliation.',
      items: [
        'Invoices, proforma, credit notes with HSN + CGST/SGST/IGST',
        'Auto place-of-supply detection (intra vs inter-state)',
        'Payments — cash, card, UPI, cheque, online',
        'EMI + installment plans with dunning reminders',
        'Estimates with patient e-acceptance workflow',
        'Write-offs with approval chain',
        'Day-end closure with cash variance reconciliation',
        'Coupons, gift cards, loyalty packages',
        'Bank reconciliation & financial controls',
        'Tally + QuickBooks + e-invoice (IRP) integrations',
      ],
    },
    {
      icon: Pill, tint: '#8b5cf6',
      title: 'Pharmacy & Inventory',
      blurb: 'FEFO batches, zero negative stock, real-time alerts.',
      items: [
        'Pharmacy POS with printable bills',
        'Medicine master with reorder levels + FEFO batching',
        'Batch tracking — expiry alerts, auto-flag stale stock',
        'Purchase orders + GRN matching',
        'Stock transfers between branches (atomic, race-safe)',
        'Stock adjustments with audit reason',
        'Dental consumables inventory',
        'Lab orders & vendor TAT tracking',
        'Supplier performance analytics',
        'Demand forecasting (seasonal-aware)',
      ],
    },
    {
      icon: Users, tint: '#ec4899',
      title: 'Staff & HR',
      blurb: 'Anti-buddy-punch attendance to full payroll run.',
      items: [
        'Attendance with geofencing + face recognition',
        'Live-liveness check + AES-256-GCM biometric encryption',
        'Shift master, swap requests, leave approvals',
        'Payroll engine — payslip PDFs, PF/ESI slabs, LOP',
        'Doctor & staff commissions (rule-based slabs)',
        'Performance incentives & bonus tracking',
        'Staff advance / loan deduction',
        'Onboarding checklist + document vault',
        'Training courses with expiry alerts',
        'Certificate management + renewal reminders',
      ],
    },
    {
      icon: MessageSquare, tint: '#14b8a6',
      title: 'Communications',
      blurb: 'Every channel your patients actually use.',
      items: [
        'WhatsApp Business API — templates, bulk, inbound AI',
        'SMS (DLT-compliant) for OTP and transactional',
        'Email with branded templates + delivery tracking',
        'VoIP click-to-call + recording + call intelligence',
        'Fax sending/receiving (TPA workflows)',
        'RCS rich cards (India-ready)',
        'Telemedicine video consults',
        'Front desk AI inbox — triages inbound messages',
        'Team chat channels',
        'Tracked numbers for campaign attribution',
      ],
    },
    {
      icon: Heart, tint: '#f43f5e',
      title: 'Patient Engagement',
      blurb: 'Retention, referrals, reviews — on autopilot.',
      items: [
        'Patient portal — bills, prescriptions, estimates',
        'Real-OTP portal login (bcrypt-hashed, 24h token)',
        'Online booking + request recalls from portal',
        'NPS feedback with sentiment analysis',
        'Google review request automation',
        'Patient-to-patient referral rewards',
        'AI-driven recare campaigns',
        'Treatment-specific WhatsApp journeys',
        'Birthday + anniversary automations (DND-aware)',
        'Dental education library for patients',
      ],
    },
    {
      icon: Shield, tint: '#3b82f6',
      title: 'Compliance & Security',
      blurb: 'NABH, HIPAA, GDPR, DPDP — built-in, not bolted-on.',
      items: [
        'NABH registers: sterilization, disinfection, waterline flush',
        'NABH incident reporting + RCA workflow',
        'Quality indicator monitoring',
        'HIPAA / GDPR / DPDP data export + right-to-erase',
        'Full audit log on every mutation',
        'Access log on sensitive PHI reads',
        'Role-based access control (7 roles)',
        '2FA / TOTP with recovery codes',
        'Device approval + fingerprint gating',
        'Webhook HMAC verification (PineLabs / Razorpay / Meta)',
      ],
    },
    {
      icon: Brain, tint: OZ.orange,
      title: 'AI & Intelligence',
      blurb: 'Veeransh AI — built for Indian dental practices.',
      items: [
        'Voice-to-SOAP clinical notes (Hinglish-aware)',
        'AI X-ray anomaly detection + caries scoring',
        'Smart treatment plan recommendations',
        'Auto-summary of patient history',
        'AI front-desk replies on WhatsApp',
        'Predictive recall scheduling',
        'Patient churn + payment-default risk scores',
        'Clinical quality KPI prediction',
        'Drug-interaction + allergy warnings',
        'Prompt-injection-hardened LLM pipeline',
      ],
    },
    {
      icon: Building2, tint: '#f59e0b',
      title: 'Multi-branch & Franchise',
      blurb: 'One or one hundred branches — same dashboard.',
      items: [
        'Branch master with per-branch rates',
        'Cross-branch patient records + transfer history',
        'Inter-branch stock + referral flows',
        'Consolidated revenue + patient count reports',
        'Per-branch doctor commission rules',
        'Franchise royalty calculation',
        'Corporate partner billing (school / insurance / TPA)',
        'Multi-tenant SaaS data isolation',
        'Super-admin panel for platform owners',
      ],
    },
    {
      icon: BarChart3, tint: '#a855f7',
      title: 'Analytics & Reports',
      blurb: 'Live metrics — not month-end spreadsheets.',
      items: [
        'Executive dashboard with KPI cards',
        'Revenue + treatment-mix analytics',
        'Patient demographic insights',
        'Clinical quality indicators (NABH-aligned)',
        'Retention & churn cohorts',
        'Inventory forecasting',
        'KPI register (custom daily/weekly/monthly)',
        'GSTR-1 / GSTR-2 / TDS / ITC reports',
        '40+ pre-built reports + Excel export',
        'Schedulable email delivery',
      ],
    },
    {
      icon: Plug, tint: '#06b6d4',
      title: 'Integrations',
      blurb: '25+ third-party services, per-tenant keys.',
      items: [
        'Razorpay — checkout + subscription billing',
        'PineLabs A910 POS (HMAC-signed webhooks)',
        'Google Drive + OneDrive cloud backup',
        'Firebase Cloud Messaging push',
        'Tally XML + QuickBooks export',
        'ABHA / e-Rupee / DigiLocker (India health stack)',
        'e-Invoice (IRP) JSON generation',
        'MSG91 SMS + Jitsi video',
        '2Factor.in voice OTP',
        'Anthropic Claude + Google Gemini for AI',
      ],
    },
  ];

  const trustLogos = ['SmileCare', 'DentAura', 'MaxDental', 'ToothClub', 'BriteOrtho', 'UrbanSmile', 'Clove+', 'Apollo Dental'];

  const tabs = [
    { id: 0, label: 'Calendar', icon: Calendar },
    { id: 1, label: 'Patients', icon: Users },
    { id: 2, label: 'Analytics', icon: BarChart3 },
    { id: 3, label: 'AI', icon: Brain },
  ];

  const testimonials = [
    {
      quote: 'We replaced 4 different tools with ORALZY. Our front desk got 3 hours of their day back.',
      name: 'Dr. Priya Malhotra', clinic: 'Malhotra Dental, Mumbai', stars: 5,
    },
    {
      quote: 'Veeransh AI drafts my clinical notes faster than I can. It feels like having an extra associate.',
      name: 'Dr. Rohan Verma', clinic: 'Smile Studio, Gurgaon', stars: 5,
    },
    {
      quote: 'NABH audit was our biggest fear. ORALZY made compliance feel automatic instead of overwhelming.',
      name: 'Dr. Anjali Iyer', clinic: 'Iyer Multi-speciality, Bengaluru', stars: 5,
    },
  ];

  const plans = [
    {
      name: 'Starter', price: '₹999', period: '/ month + GST',
      desc: 'Small clinics starting their digital journey.',
      features: ['2 users', 'Up to 500 patients', 'Scheduling & billing', '500 WhatsApp msgs/mo', 'Lab orders', 'Email support'],
      cta: 'Start free trial', highlight: false,
    },
    {
      name: 'Pro', price: '₹2,499', period: '/ month + GST',
      desc: 'Growing clinics that need AI, mobile & unlimited patients.',
      features: ['10 users', 'Unlimited patients', 'Veeransh AI included', 'Mobile app', 'Multi-doctor', 'Unlimited WhatsApp', 'Pharmacy + Insurance', 'Priority support'],
      cta: 'Start free trial', highlight: true,
    },
    {
      name: 'Enterprise', price: 'Custom', period: '',
      desc: 'Multi-branch chains and dental groups.',
      features: ['Everything in Pro', 'Unlimited branches', 'Custom branding', 'NABH & audit support', 'Dedicated account manager', 'On-site training', 'Data export & API'],
      cta: 'Talk to sales', highlight: false,
    },
  ];

  const faqs = [
    { q: 'How long does it take to onboard my clinic?', a: 'Most practices are live within one working day. Our team handles the import of your existing patient records, prescriptions and billing history — you just show up for a 30-minute onboarding call.' },
    { q: 'Is my data safe?', a: 'Absolutely. ORALZY uses 256-bit AES encryption at rest, TLS 1.3 in transit, and is hosted on ISO-27001 certified infrastructure. Your data is backed up every 6 hours across multiple regions.' },
    { q: 'Does Veeransh AI replace my staff?', a: 'No — it amplifies them. Veeransh handles the repetitive drafting, summarising and searching so your team can focus on patients. Think of it as an extra associate that never sleeps.' },
    { q: 'Can I try it before paying?', a: 'Yes. Every plan comes with a 14-day free trial, no credit card required. You get full access to the platform including Veeransh AI.' },
    { q: 'Do you support multi-branch clinics?', a: 'Yes. The Practice and Scale plans include full multi-branch support with role-based access, consolidated reporting, and branch-level analytics.' },
    { q: 'Is ORALZY NABH compliant?', a: 'ORALZY ships with NABH-ready SOPs, registers, audit logs and compliance modules. We also support clinics during their first accreditation cycle.' },
  ];

  return (
    <div style={styles.root}>
      <style>{css}</style>

      {/* Scroll progress bar */}
      <div style={{ ...styles.scrollBar, width: `${scrollPct}%` }} />

      {/* Animated background orbs + grid */}
      <div style={styles.bgLayer} aria-hidden="true">
        <div className="oz-orb oz-orb-1" style={{ transform: `translate3d(${pX * 0.6}px, ${pY * 0.6 - scrollY * 0.15}px, 0)` }} />
        <div className="oz-orb oz-orb-2" style={{ transform: `translate3d(${-pX * 0.4}px, ${-pY * 0.4 - scrollY * 0.2}px, 0)` }} />
        <div className="oz-orb oz-orb-3" style={{ transform: `translate3d(${pX * 0.3}px, ${pY * 0.3 - scrollY * 0.1}px, 0)` }} />
        <div className="oz-grid" />
        <div className="oz-noise" />
      </div>

      {/* ─────────── NAV ─────────── */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.logo}>
            <OralzyLogo height={34} onDark />
          </div>
          <div style={styles.navLinks} className="oz-nav-links">
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#all-features" style={styles.navLink}>All Modules</a>
            <a href="#demo" style={styles.navLink}>Product</a>
            <a href="#pricing" style={styles.navLink}>Pricing</a>
            <a href="#faq" style={styles.navLink}>FAQ</a>
          </div>
          <div style={styles.navCtas}>
            <button onClick={onSignIn} style={styles.btnGhost} className="oz-btn-ghost">
              <LogIn size={15} /> Log in
            </button>
            <button onClick={onGetStarted} style={styles.btnPrimary} className="oz-btn-primary">
              Get started <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </nav>

      {/* ─────────── HERO ─────────── */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.heroBadge} className="oz-fade-up">
            <span style={styles.heroBadgeDot} /> Now with Veeransh AI · built for Indian dental practices
          </div>

          <h1 style={styles.heroTitle} className="oz-fade-up oz-delay-1">
            The{' '}
            <span style={styles.rotatingWrap} aria-live="polite">
              <span key={wordIdx} className="oz-word-rotate" style={styles.gradText}>
                {ROTATING_WORDS[wordIdx]}
              </span>
              <span className="oz-caret" />
            </span>
            <br /> for modern dental practices.
          </h1>

          <p style={styles.heroSub} className="oz-fade-up oz-delay-2">
            ORALZY unifies scheduling, clinical records, billing, marketing and AI —
            so your clinic runs faster, cleaner, and smarter than ever before.
          </p>

          <div style={styles.heroCtas} className="oz-fade-up oz-delay-3">
            <button onClick={onGetStarted} style={styles.btnPrimaryLg} className="oz-btn-primary-lg">
              Start free trial <ArrowRight size={18} />
            </button>
            <button onClick={onSignIn} style={styles.btnGhostLg} className="oz-btn-ghost-lg">
              <LogIn size={17} /> I already have an account
            </button>
          </div>

          {/* Mini social proof */}
          <div style={styles.socialProof} className="oz-fade-up oz-delay-3">
            <div style={styles.avatarStack}>
              {['#F57C1F', '#29A8E0', '#f59e0b', '#ec4899', '#10b981'].map((c, i) => (
                <div key={i} style={{ ...styles.avatar, background: c, zIndex: 5 - i }} />
              ))}
            </div>
            <span style={styles.socialText}>
              Trusted by <strong style={{ color: '#fff' }}>500+ clinics</strong> across India
            </span>
            <span style={styles.starRow}>
              {[0,1,2,3,4].map(i => <Star key={i} size={13} fill="#fbbf24" color="#fbbf24" />)}
              <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 4 }}>4.9/5</span>
            </span>
          </div>

          {/* Floating product preview card */}
          <div
            style={{
              ...styles.heroPreview,
              transform: `perspective(1200px) rotateX(${8 - pY * 0.05}deg) rotateY(${pX * 0.05}deg)`,
            }}
            className="oz-fade-up oz-delay-4 oz-hero-preview"
          >
            <div style={styles.previewChrome}>
              <span style={{ ...styles.previewDot, background: '#ff5f57' }} />
              <span style={{ ...styles.previewDot, background: '#febc2e' }} />
              <span style={{ ...styles.previewDot, background: '#28c840' }} />
              <div style={styles.previewUrl}>oralzy.com / dashboard</div>
            </div>
            <div style={styles.previewBody}>
              <div style={styles.previewSidebar}>
                {['Dashboard', 'Calendar', 'Patients', 'Analytics', 'AI'].map((l, i) => (
                  <div key={l} style={{ ...styles.previewNavItem, ...(i === 0 ? styles.previewNavActive : {}) }}>
                    <span style={styles.previewNavDot} /> {l}
                  </div>
                ))}
              </div>
              <div style={styles.previewContent}>
                <div style={styles.previewStats}>
                  {['Patients','Revenue','Appointments','Retention'].map((s, i) => (
                    <div key={s} style={styles.previewStatCard}>
                      <div style={styles.previewStatLabel}>{s}</div>
                      <div style={styles.previewStatValue}>{['2,481','₹4.2L','128','94%'][i]}</div>
                      <div style={{ ...styles.previewBar, width: ['78%','64%','52%','88%'][i] }} />
                    </div>
                  ))}
                </div>
                <div style={styles.previewChart}>
                  <div style={styles.previewChartTitle}>Revenue trend · last 30 days</div>
                  <svg viewBox="0 0 400 120" style={{ width: '100%', height: '100%' }}>
                    <defs>
                      <linearGradient id="oz-grad" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#F57C1F" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#F57C1F" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,90 C40,70 80,95 120,60 C160,25 200,70 240,50 C280,30 320,55 360,20 L400,10 L400,120 L0,120 Z" fill="url(#oz-grad)" />
                    <path d="M0,90 C40,70 80,95 120,60 C160,25 200,70 240,50 C280,30 320,55 360,20 L400,10" fill="none" stroke="#F57C1F" strokeWidth="2.5" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── LOGO MARQUEE ─────────── */}
      <section style={styles.marqueeSection}>
        <div style={styles.container}>
          <div style={styles.marqueeLabel}>Powering practices across 80+ Indian cities</div>
        </div>
        <div style={styles.marqueeTrack}>
          <div className="oz-marquee">
            {[...trustLogos, ...trustLogos, ...trustLogos].map((l, i) => (
              <span key={i} style={styles.marqueeItem}>{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── BENTO FEATURES — LIGHT SECTION ─────────── */}
      <section id="features" style={styles.lightSection}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.sectionHeadLight}>
            <div style={styles.eyebrowLight}>Capabilities</div>
            <h2 style={styles.sectionTitleLight}>
              Every tool your practice needs —
              <br /> <span style={styles.gradTextLight}>woven into one platform.</span>
            </h2>
            <p style={styles.sectionSubLight}>
              From the front desk to the operatory, ORALZY replaces a dozen disconnected apps.
            </p>
          </div>

          <div className="oz-bento-grid" style={styles.bentoGrid}>
            {bento.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  ref={addReveal}
                  className="oz-reveal oz-bento-card"
                  style={{
                    ...styles.bentoCard,
                    gridColumn: `span ${f.span}`,
                    transitionDelay: `${i * 70}ms`,
                    '--tint': f.tint,
                  }}
                >
                  <div style={{ ...styles.bentoIcon, background: `${f.tint}18`, color: f.tint, borderColor: `${f.tint}40` }}>
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <div style={styles.bentoEyebrow}>{f.eyebrow}</div>
                  <div style={styles.bentoTitle}>{f.title}</div>
                  <div style={styles.bentoDesc}>{f.desc}</div>
                  <BentoVisual kind={f.visual} tint={f.tint} />
                  <div style={{ ...styles.bentoGlow, background: `radial-gradient(400px circle at var(--mx, 50%) var(--my, 50%), ${f.tint}25, transparent 40%)` }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── FEATURE UNIVERSE — DARK ─────────── */}
      {/* 12 category cards covering the entire platform. Shows depth without
          forcing the visitor to click through a dozen sub-pages. Each card is
          a mini-directory — icon, blurb, and every module inside that domain. */}
      <section id="all-features" style={styles.universeSection}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.sectionHead}>
            <div style={styles.eyebrow}><Sparkles size={13} style={{ marginRight: 4 }} /> The full platform</div>
            <h2 style={styles.sectionTitle}>
              Every module your clinic will ever need,
              <br /> <span style={styles.gradText}>in one place.</span>
            </h2>
            <p style={styles.sectionSub}>
              12 domains. 120+ features. Hundreds of endpoints. All under a single login,
              with the same data model end-to-end.
            </p>
          </div>

          <div className="oz-universe-stats" style={styles.universeStats}>
            {[
              { n: 120, suffix: '+', label: 'Features' },
              { n: 60,  suffix: '+', label: 'Modules'  },
              { n: 25,  suffix: '+', label: 'Integrations' },
              { n: 7,   suffix: '',  label: 'Compliance standards' },
            ].map((s) => (
              <div key={s.label} style={styles.universeStatCell}>
                <div className="oz-universe-stat-value" style={styles.universeStatValue}>
                  <Counter to={s.n} suffix={s.suffix} />
                </div>
                <div style={styles.universeStatLabel}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="oz-universe-grid" style={styles.universeGrid}>
            {featureUniverse.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.title}
                  ref={addReveal}
                  className="oz-reveal oz-universe-card"
                  style={{
                    ...styles.universeCard,
                    transitionDelay: `${(i % 4) * 70}ms`,
                    '--tint': cat.tint,
                  }}
                >
                  <div style={styles.universeCardHead}>
                    <div style={{
                      ...styles.universeIcon,
                      background: `${cat.tint}18`,
                      color: cat.tint,
                      borderColor: `${cat.tint}40`,
                    }}>
                      <Icon size={20} strokeWidth={2.2} />
                    </div>
                    <div>
                      <div style={styles.universeTitle}>{cat.title}</div>
                      <div style={styles.universeBlurb}>{cat.blurb}</div>
                    </div>
                  </div>
                  <ul style={styles.universeList}>
                    {cat.items.map((it) => (
                      <li key={it} style={styles.universeItem}>
                        <Check size={13} color={cat.tint} strokeWidth={3} style={{ flexShrink: 0, marginTop: 3 }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div style={styles.universeFooter}>
                    <span style={{ color: cat.tint }}>{cat.items.length} tools</span>
                    <span style={styles.universeFooterArrow}>→</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={styles.universeCta}>
            <p style={styles.universeCtaText}>
              And we ship new modules every month. The roadmap is public, voted by our customers.
            </p>
            <button onClick={onGetStarted} style={styles.btnPrimaryLg} className="oz-btn-primary-lg">
              Explore the full platform <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </section>

      {/* ─────────── ANIMATED STATS — LIGHT GRADIENT ─────────── */}
      <section style={styles.statsSectionLight}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.statsHead}>
            <div style={styles.eyebrowLight}>By the numbers</div>
            <h2 style={styles.sectionTitleLight}>
              Built for scale.<br />
              <span style={styles.gradTextLight}>Tested by the best.</span>
            </h2>
          </div>

          <div className="oz-stats-grid" style={styles.statsGridLight}>
            {[
              { value: 500, suffix: '+', label: 'Clinics onboarded', icon: Stethoscope, tint: '#F57C1F' },
              { value: 2, suffix: 'M+', label: 'Patients managed', icon: Users, tint: '#29A8E0' },
              { value: 99.9, suffix: '%', label: 'Uptime SLA', icon: TrendingUp, tint: '#10b981' },
              { value: 256, suffix: '-bit', label: 'Encryption', icon: Lock, tint: '#f59e0b' },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} style={styles.statCellLight}>
                  <div style={{ ...styles.statIconLight, background: `${s.tint}18`, color: s.tint }}>
                    <Icon size={20} />
                  </div>
                  <div style={styles.statValueLight}>
                    <Counter to={s.value} suffix={s.suffix} />
                  </div>
                  <div style={styles.statLabelLight}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── BIG AI SPOTLIGHT — DARK ─────────── */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal oz-spotlight" style={styles.spotlight}>
            <div>
              <div style={styles.eyebrow}><Cpu size={13} style={{ marginRight: 4 }} /> Veeransh AI</div>
              <h2 style={styles.sectionTitleSm}>
                An <span style={styles.gradText}>AI co-pilot</span> that actually understands dentistry.
              </h2>
              <p style={styles.sectionSub}>
                Voice-dictate SOAP notes. Auto-summarise patient history. Draft prescriptions instantly.
                Detect anomalies in X-rays. Veeransh AI learns your practice and becomes faster every day.
              </p>
              <ul style={styles.checkList}>
                {[
                  'Natural-language patient search across every record',
                  'AI-generated treatment plans reviewed by you',
                  'Automatic WhatsApp reply drafting',
                  'Smart reminders based on treatment outcomes',
                ].map((t) => (
                  <li key={t} style={styles.checkItem}>
                    <CheckCircle2 size={17} color="#F57C1F" /> {t}
                  </li>
                ))}
              </ul>
              <button onClick={onGetStarted} style={styles.btnPrimaryLg} className="oz-btn-primary-lg">
                Try it free <ArrowRight size={17} />
              </button>
            </div>

            <div style={styles.spotlightRight} className="oz-float">
              <div style={styles.aiCard}>
                <div style={styles.aiCardHead}>
                  <div style={styles.aiAvatar}><Brain size={16} color="#fff" /></div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Veeransh AI</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Online · Your dental co-pilot</div>
                  </div>
                </div>
                <div style={styles.aiMsgUser}>Summarise Priya Sharma's last 3 visits.</div>
                <div style={styles.aiMsgBot}>
                  <strong>Priya Sharma · UHID SDH-0421</strong><br/>
                  3 visits in last 6 months. Ongoing: RCT on 16 (2/3 sittings done).
                  Last Rx: Amoxicillin 500mg × 5 days. Next recall: <strong>2 Apr 2026</strong>.
                  <div style={styles.aiChips}>
                    <span style={styles.aiChip}>Open chart</span>
                    <span style={styles.aiChip}>Send reminder</span>
                    <span style={styles.aiChip}>Book follow-up</span>
                  </div>
                </div>
                <div style={styles.aiInput}>
                  <Zap size={14} color="#F57C1F" />
                  <span style={{ color: '#9ca3af', fontSize: 13 }}>Ask anything about your practice…</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── INTERACTIVE PRODUCT TABS — DARK ─────────── */}
      <section id="demo" style={styles.section}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.sectionHead}>
            <div style={styles.eyebrow}>Product</div>
            <h2 style={styles.sectionTitle}>See ORALZY <span style={styles.gradText}>in motion.</span></h2>
            <p style={styles.sectionSub}>Switch between modules and watch the platform come alive.</p>
          </div>

          <div ref={addReveal} className="oz-reveal" style={styles.tabsWrap}>
            <div style={styles.tabsBar}>
              {tabs.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    style={{
                      ...styles.tabBtn,
                      ...(activeTab === t.id ? styles.tabBtnActive : {}),
                    }}
                  >
                    <Icon size={16} /> {t.label}
                  </button>
                );
              })}
            </div>
            <div style={styles.tabsPanel}>
              <div key={activeTab} className="oz-tab-fade" style={styles.tabsContent}>
                {activeTab === 0 && <TabCalendar />}
                {activeTab === 1 && <TabPatients />}
                {activeTab === 2 && <TabAnalytics />}
                {activeTab === 3 && <TabAI />}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── TESTIMONIALS — LIGHT ─────────── */}
      <section style={styles.lightSection}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.sectionHeadLight}>
            <div style={styles.eyebrowLight}>Loved by dentists</div>
            <h2 style={styles.sectionTitleLight}>
              Doctors don't just use ORALZY —
              <br /> <span style={styles.gradTextLight}>they rave about it.</span>
            </h2>
          </div>

          <div className="oz-testimonial-grid" style={styles.testimonialGrid}>
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                ref={addReveal}
                className="oz-reveal"
                style={{ ...styles.testimonialCard, transitionDelay: `${i * 100}ms` }}
              >
                <div style={styles.testimonialStars}>
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} size={15} fill="#fbbf24" color="#fbbf24" />
                  ))}
                </div>
                <p style={styles.testimonialQuote}>"{t.quote}"</p>
                <div style={styles.testimonialMeta}>
                  <div style={{ ...styles.testimonialAvatar, background: ['#F57C1F','#29A8E0','#ec4899'][i] }}>
                    {t.name.split(' ')[1][0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{t.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12.5 }}>{t.clinic}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── PRICING — LIGHT GRADIENT ─────────── */}
      <section id="pricing" style={styles.pricingSection}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.sectionHeadLight}>
            <div style={styles.eyebrowLight}>Pricing</div>
            <h2 style={styles.sectionTitleLight}>
              Simple pricing.<br />
              <span style={styles.gradTextLight}>Honest value.</span>
            </h2>
            <p style={styles.sectionSubLight}>
              14-day free trial on every plan. No credit card required. Cancel anytime.
            </p>
          </div>

          <div className="oz-pricing-grid" style={styles.pricingGrid}>
            {plans.map((p, i) => (
              <div
                key={p.name}
                ref={addReveal}
                className={`oz-reveal ${p.highlight ? 'oz-plan-highlight' : ''}`}
                style={{
                  ...styles.planCard,
                  ...(p.highlight ? styles.planCardHighlight : {}),
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                {p.highlight && <div style={styles.planBadge}>Most popular</div>}
                <div style={styles.planName}>{p.name}</div>
                <div style={styles.planPrice}>
                  <span style={styles.planPriceVal}>{p.price}</span>
                  <span style={styles.planPriceP}>{p.period}</span>
                </div>
                <div style={styles.planDesc}>{p.desc}</div>
                <button
                  onClick={p.cta === 'Talk to sales' ? onGetStarted : onGetStarted}
                  style={p.highlight ? styles.btnPrimaryLg : styles.btnPlanGhost}
                  className={p.highlight ? 'oz-btn-primary-lg' : ''}
                >
                  {p.cta} <ArrowRight size={15} />
                </button>
                <ul style={styles.planFeatures}>
                  {p.features.map((f) => (
                    <li key={f} style={styles.planFeature}>
                      <Check size={15} color="#F57C1F" strokeWidth={3} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FAQ — DARK ─────────── */}
      <section id="faq" style={styles.section}>
        <div style={{ ...styles.container, maxWidth: 820 }}>
          <div ref={addReveal} className="oz-reveal" style={styles.sectionHead}>
            <div style={styles.eyebrow}>FAQ</div>
            <h2 style={styles.sectionTitle}>Got questions? <span style={styles.gradText}>We've got answers.</span></h2>
          </div>

          <div ref={addReveal} className="oz-reveal" style={styles.faqWrap}>
            {faqs.map((f, i) => (
              <FAQItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FINAL CTA ─────────── */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <div ref={addReveal} className="oz-reveal" style={styles.ctaCard}>
            <div style={styles.ctaGlow} />
            <Rocket size={36} color="#F57C1F" style={{ marginBottom: 18, position: 'relative' }} />
            <h2 style={styles.ctaTitle}>Ready to future-proof your clinic?</h2>
            <p style={styles.ctaSub}>
              Join the dental practices already running on ORALZY.
              Free 14-day trial — no credit card required.
            </p>
            <div style={styles.heroCtas}>
              <button onClick={onGetStarted} style={styles.btnPrimaryLg} className="oz-btn-primary-lg">
                <UserPlus size={17} /> Create your account
              </button>
              <button onClick={onSignIn} style={styles.btnGhostLg} className="oz-btn-ghost-lg">
                <LogIn size={17} /> Sign in
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── FOOTER ─────────── */}
      <footer style={styles.footer}>
        <div style={styles.container}>
          <div style={styles.footerInner}>
            <OralzyLogo height={28} onDark />
            <div style={styles.footerMeta}>
              © {new Date().getFullYear()} ORALZY · Dental Clinic Software
              <span style={{ margin: '0 8px', opacity: 0.4 }}>|</span>
              <a href="#privacy" onClick={e => { e.preventDefault(); setFooterPage('privacy'); }} style={{ color: '#94a3b8', textDecoration: 'none' }}>Privacy Policy</a>
              <span style={{ margin: '0 8px', opacity: 0.4 }}>|</span>
              <a href="#terms" onClick={e => { e.preventDefault(); setFooterPage('terms'); }} style={{ color: '#94a3b8', textDecoration: 'none' }}>Terms of Service</a>
              <span style={{ margin: '0 8px', opacity: 0.4 }}>|</span>
              <a href="#refund" onClick={e => { e.preventDefault(); setFooterPage('refund'); }} style={{ color: '#94a3b8', textDecoration: 'none' }}>Refund Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      {footerPage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setFooterPage(null)}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 700, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: '36px 32px', color: '#1F2937', lineHeight: 1.7 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setFooterPage(null)} style={{ float: 'right', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#999' }}>×</button>
            {footerPage === 'privacy' && (<>
              <h2>Privacy Policy</h2>
              <p><strong>Last updated:</strong> April 2026</p>
              <p>ORALZY ("we", "our") operates oralzy.com. This policy describes how we collect, use, and protect your data.</p>
              <h3>Data We Collect</h3>
              <ul><li>Clinic info (name, email, phone) during signup</li><li>Patient records entered by your clinic staff</li><li>Usage analytics (page views, feature usage — anonymized)</li></ul>
              <h3>How We Use Data</h3>
              <ul><li>Provide and improve our service</li><li>Send transactional emails (OTP, billing, reminders)</li><li>Customer support</li></ul>
              <h3>Data Storage & Security</h3>
              <ul><li>AES-256 encryption at rest for credentials</li><li>TLS 1.3 in transit</li><li>MongoDB Atlas hosted in Mumbai, India (DPDP Act compliant)</li><li>Daily automated backups</li></ul>
              <h3>Your Rights (DPDP Act 2023)</h3>
              <ul><li>Right to access your data</li><li>Right to correction</li><li>Right to erasure (subject to NABH retention requirements)</li><li>Right to data portability</li></ul>
              <h3>Contact</h3>
              <p>Email: privacy@oralzy.com</p>
            </>)}
            {footerPage === 'terms' && (<>
              <h2>Terms of Service</h2>
              <p><strong>Last updated:</strong> April 2026</p>
              <h3>Service</h3><p>ORALZY provides cloud-based dental clinic management software on a subscription basis.</p>
              <h3>Account</h3><p>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</p>
              <h3>Subscription & Billing</h3><p>Subscriptions are billed monthly via Razorpay. 18% GST applies. You may cancel anytime — access continues until the end of the billing period.</p>
              <h3>Data Ownership</h3><p>You own your clinic data. We are a processor, not the owner. Upon cancellation, you may request a full data export within 90 days.</p>
              <h3>Uptime</h3><p>We target 99.9% uptime but do not guarantee it. Scheduled maintenance windows will be communicated 48 hours in advance.</p>
              <h3>Limitation of Liability</h3><p>ORALZY liability is limited to the subscription fees paid in the preceding 12 months.</p>
            </>)}
            {footerPage === 'refund' && (<>
              <h2>Refund Policy</h2>
              <p><strong>Last updated:</strong> April 2026</p>
              <p>Free trial: No payment required. Cancel anytime during trial at no cost.</p>
              <p>Monthly subscription: No refunds for partial months. If you cancel, your access continues until the billing period ends.</p>
              <p>Yearly subscription: Pro-rated refund available within first 30 days. After 30 days, no refunds.</p>
              <p>Disputes: Contact billing@oralzy.com within 7 days of charge.</p>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────── Bento visuals (small inline mini-widgets) ─────────────── */

const BentoVisual = ({ kind, tint }) => {
  const style = { marginTop: 18, position: 'relative', zIndex: 1 };
  if (kind === 'ai') {
    return (
      <div style={{ ...style, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['Summarise visit', 'Draft Rx', 'Find patient', 'Voice note'].map((c) => (
          <span key={c} style={{
            padding: '6px 12px', borderRadius: 999,
            background: `${tint}15`, color: tint,
            fontSize: 12, fontWeight: 600, border: `1px solid ${tint}30`,
          }}>{c}</span>
        ))}
      </div>
    );
  }
  if (kind === 'calendar') {
    return (
      <div style={{ ...style, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {Array.from({ length: 21 }).map((_, i) => (
          <div key={i} style={{
            height: 18, borderRadius: 3,
            background: [5,8,12,16].includes(i) ? tint : `${tint}20`,
            opacity: [5,8,12,16].includes(i) ? 1 : 0.6,
          }} />
        ))}
      </div>
    );
  }
  if (kind === 'chart') {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'flex-end', gap: 6, height: 60 }}>
        {[30, 50, 40, 70, 60, 85, 75].map((h, i) => (
          <div key={i} style={{
            flex: 1, height: `${h}%`, borderRadius: 3,
            background: `linear-gradient(180deg, ${tint}, ${tint}40)`,
          }} />
        ))}
      </div>
    );
  }
  if (kind === 'chat') {
    return (
      <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ alignSelf: 'flex-start', padding: '6px 10px', borderRadius: '10px 10px 10px 2px', background: '#f1f5f9', fontSize: 12, color: '#334155', maxWidth: '75%' }}>
          Hi Dr. Sharma, reminder for your 5 PM appointment
        </div>
        <div style={{ alignSelf: 'flex-end', padding: '6px 10px', borderRadius: '10px 10px 2px 10px', background: tint, color: '#fff', fontSize: 12, maxWidth: '60%' }}>
          See you soon! ✓
        </div>
      </div>
    );
  }
  if (kind === 'shield') {
    return (
      <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {['AAC · Scope of services', 'COP · Care of patients', 'IMS · Infection mgmt'].map((t) => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#475569' }}>
            <CheckCircle2 size={14} color={tint} /> {t}
          </div>
        ))}
      </div>
    );
  }
  if (kind === 'patient') {
    return (
      <div style={{ ...style, display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${tint}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tint, fontWeight: 700 }}>PS</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>Priya Sharma</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {['X-ray', 'Rx', 'Bill', 'Notes'].map((c) => (
              <span key={c} style={{ padding: '2px 8px', borderRadius: 999, background: '#f1f5f9', color: '#475569', fontSize: 10.5, fontWeight: 600 }}>{c}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

/* ─────────────── Tab panels ─────────────── */

const TabCalendar = () => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10, padding: 24 }}>
    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
      <div key={d} style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{d}</div>
    ))}
    {Array.from({ length: 28 }).map((_, i) => (
      <div key={i} style={{
        height: 70, borderRadius: 8, padding: 8,
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        fontSize: 11, color: '#94a3b8',
        position: 'relative',
      }}>
        <div style={{ fontWeight: 700, color: '#fff', marginBottom: 4 }}>{i+1}</div>
        {i % 4 === 1 && <div style={{ padding: '2px 5px', borderRadius: 3, background: '#F57C1F30', color: '#7DC9EA', fontSize: 9.5 }}>RCT · 10am</div>}
        {i % 5 === 2 && <div style={{ padding: '2px 5px', borderRadius: 3, background: '#29A8E030', color: '#c4b5fd', fontSize: 9.5, marginTop: 3 }}>Cleaning</div>}
      </div>
    ))}
  </div>
);

const TabPatients = () => (
  <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
    {[
      { n: 'Priya Sharma', u: 'SDH-0421', t: 'RCT · Ongoing', c: '#F57C1F' },
      { n: 'Rohan Verma', u: 'SDH-0388', t: 'Cleaning · Completed', c: '#10b981' },
      { n: 'Anjali Iyer', u: 'SDH-0317', t: 'Implant · Follow-up', c: '#29A8E0' },
      { n: 'Karthik Rao', u: 'SDH-0289', t: 'Ortho · Monthly', c: '#f59e0b' },
    ].map((p) => (
      <div key={p.u} style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: 14, borderRadius: 10,
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: p.c, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>
          {p.n.split(' ').map(x=>x[0]).join('')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{p.n}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{p.u} · {p.t}</div>
        </div>
        <span style={{ color: '#7DC9EA', fontSize: 12 }}>Open →</span>
      </div>
    ))}
  </div>
);

const TabAnalytics = () => (
  <div style={{ padding: 24 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
      {[
        { l: 'Revenue', v: '₹4.82L', d: '+12%' },
        { l: 'Patients', v: '2,481', d: '+8%' },
        { l: 'Avg Ticket', v: '₹1,940', d: '+4%' },
        { l: 'Retention', v: '94%', d: '+2%' },
      ].map((s) => (
        <div key={s.l} style={{ padding: 14, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{s.l}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{s.v}</div>
          <div style={{ fontSize: 11, color: '#10b981', marginTop: 4 }}>↑ {s.d}</div>
        </div>
      ))}
    </div>
    <div style={{ padding: 16, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <svg viewBox="0 0 600 140" style={{ width: '100%', height: 140 }}>
        <defs>
          <linearGradient id="a2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#F57C1F" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#F57C1F" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,100 C60,80 120,110 180,65 C240,20 300,80 360,55 C420,30 480,75 540,25 L600,10 L600,140 L0,140 Z" fill="url(#a2)" />
        <path d="M0,100 C60,80 120,110 180,65 C240,20 300,80 360,55 C420,30 480,75 540,25 L600,10" fill="none" stroke="#F57C1F" strokeWidth="2.5" />
      </svg>
    </div>
  </div>
);

const TabAI = () => (
  <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
    {[
      { role: 'user', t: 'Who are my top 5 revenue patients this quarter?' },
      { role: 'ai', t: '1. Mehta Family · ₹1.2L\n2. R. Verma · ₹88k\n3. Iyer · ₹71k\n4. Kapoor · ₹64k\n5. Desai · ₹59k' },
      { role: 'user', t: 'Draft a recall message for Mrs. Iyer in Hindi.' },
      { role: 'ai', t: 'Namaste Anjali ji, ORALZY Dental se reminder — aapki 6-monthly cleaning due hai. Book karne ke liye link: oralzy.me/b/iyer ✨' },
    ].map((m, i) => (
      <div key={i} style={{
        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
        maxWidth: '75%',
        padding: '10px 14px',
        borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        background: m.role === 'user' ? 'rgba(245,124,31,0.25)' : 'rgba(255,255,255,0.05)',
        color: m.role === 'user' ? '#7DC9EA' : '#e2e8f0',
        fontSize: 13.5, whiteSpace: 'pre-line', lineHeight: 1.55,
        border: '1px solid rgba(255,255,255,0.06)',
      }}>{m.t}</div>
    ))}
  </div>
);

/* ─────────────────────────── STYLES ─────────────────────────── */

const styles = {
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0a0f1e 0%, #0e1530 40%, #0a0f1e 100%)',
    color: '#e2e8f0',
    // Match the global stack (index.css) so mobile Safari uses the same font
    // as desktop — otherwise Inter isn't applied here and the browser falls
    // back to Times-like defaults on iOS while loading.
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
    position: 'relative',
    WebkitFontSmoothing: 'antialiased',
    WebkitTextSizeAdjust: '100%',
    textSizeAdjust: '100%',
  },
  scrollBar: {
    position: 'fixed', top: 0, left: 0, height: 3, zIndex: 100,
    background: 'linear-gradient(90deg, #F57C1F, #7DC9EA, #29A8E0)',
    boxShadow: '0 0 10px #F57C1F',
    transition: 'width 0.1s linear',
  },
  bgLayer: { position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' },
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 2 },

  /* NAV */
  nav: {
    position: 'sticky', top: 0, zIndex: 50,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    background: 'rgba(10, 15, 30, 0.65)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  navInner: { maxWidth: 1280, margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 },
  logo: { display: 'flex', alignItems: 'center', gap: 10 },
  logoMark: {
    width: 32, height: 32, borderRadius: 9,
    background: 'linear-gradient(135deg, #F57C1F 0%, #DC6A0F 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 6px 18px rgba(245,124,31,0.45)',
  },
  logoText: { color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: 1.5 },
  navLinks: { display: 'flex', alignItems: 'center', gap: 28 },
  navLink: { color: '#94a3b8', textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' },
  navCtas: { display: 'flex', alignItems: 'center', gap: 10 },

  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '9px 16px', borderRadius: 999,
    background: 'linear-gradient(135deg, #F57C1F 0%, #DC6A0F 100%)',
    color: '#fff', border: 'none', fontWeight: 600, fontSize: 13,
    cursor: 'pointer', boxShadow: '0 6px 20px rgba(245,124,31,0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 14px', borderRadius: 999,
    background: 'transparent', color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.15)', fontWeight: 500, fontSize: 13,
    cursor: 'pointer', transition: 'all 0.2s',
  },
  btnPrimaryLg: {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    padding: '15px 28px', borderRadius: 999,
    background: 'linear-gradient(135deg, #F57C1F 0%, #DC6A0F 100%)',
    color: '#fff', border: 'none', fontWeight: 600, fontSize: 15,
    cursor: 'pointer', boxShadow: '0 12px 32px rgba(245,124,31,0.45)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  btnGhostLg: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '15px 24px', borderRadius: 999,
    background: 'rgba(255,255,255,0.04)', color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.12)', fontWeight: 500, fontSize: 15,
    cursor: 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(10px)',
  },
  btnPlanGhost: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    width: '100%',
    padding: '13px 20px', borderRadius: 999,
    background: '#0f172a', color: '#fff',
    border: 'none', fontWeight: 600, fontSize: 14,
    cursor: 'pointer', transition: 'all 0.2s',
  },

  /* HERO */
  hero: { position: 'relative', padding: '80px 24px 80px', zIndex: 2 },
  heroInner: { maxWidth: 1100, margin: '0 auto', textAlign: 'center' },
  heroBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '8px 16px', borderRadius: 999,
    background: 'rgba(245, 124, 31, 0.1)',
    border: '1px solid rgba(245, 124, 31, 0.3)',
    color: '#7DC9EA', fontSize: 12.5, fontWeight: 500, marginBottom: 32,
  },
  heroBadgeDot: {
    width: 6, height: 6, borderRadius: '50%', background: '#F57C1F',
    boxShadow: '0 0 10px #F57C1F', animation: 'oz-pulse 2s infinite',
  },
  heroTitle: {
    fontSize: 'clamp(40px, 7vw, 84px)', fontWeight: 800, lineHeight: 1.05,
    letterSpacing: '-0.03em', color: '#fff', margin: '0 0 24px',
  },
  gradText: {
    background: 'linear-gradient(135deg, #F57C1F 0%, #7DC9EA 50%, #29A8E0 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text', color: 'transparent',
  },
  gradTextLight: {
    background: 'linear-gradient(135deg, #DC6A0F 0%, #F57C1F 40%, #6366f1 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text', color: 'transparent',
  },
  rotatingWrap: {
    display: 'inline-block', position: 'relative',
    perspective: '800px', whiteSpace: 'nowrap', verticalAlign: 'baseline',
  },
  heroSub: { fontSize: 'clamp(16px, 1.6vw, 20px)', color: '#94a3b8', maxWidth: 680, margin: '0 auto 36px', lineHeight: 1.55, fontWeight: 400 },
  heroCtas: { display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' },
  socialProof: {
    marginTop: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 14, flexWrap: 'wrap',
  },
  avatarStack: { display: 'flex' },
  avatar: {
    width: 30, height: 30, borderRadius: '50%',
    border: '2px solid #0a0f1e', marginLeft: -8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  },
  socialText: { color: '#94a3b8', fontSize: 13.5 },
  starRow: { display: 'inline-flex', alignItems: 'center', gap: 2 },

  heroPreview: {
    marginTop: 70, maxWidth: 1000, marginLeft: 'auto', marginRight: 'auto',
    borderRadius: 16, overflow: 'hidden',
    background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 60px 120px -40px rgba(245,124,31,0.3), 0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05) inset',
    transition: 'transform 0.2s ease-out',
  },
  previewChrome: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' },
  previewDot: { width: 11, height: 11, borderRadius: '50%' },
  previewUrl: { marginLeft: 16, padding: '4px 14px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#64748b', fontSize: 11.5, fontFamily: 'monospace' },
  previewBody: { display: 'flex', minHeight: 340 },
  previewSidebar: { width: 180, padding: 16, borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: 4 },
  previewNavItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, fontSize: 12.5, color: '#94a3b8', cursor: 'default' },
  previewNavActive: { background: 'rgba(245, 124, 31, 0.12)', color: '#7DC9EA', boxShadow: 'inset 0 0 0 1px rgba(245,124,31,0.25)' },
  previewNavDot: { width: 6, height: 6, borderRadius: '50%', background: 'currentColor' },
  previewContent: { flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
  previewStats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 },
  previewStatCard: { padding: 14, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' },
  previewStatLabel: { fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  previewStatValue: { fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 8 },
  previewBar: { height: 3, borderRadius: 2, background: 'linear-gradient(90deg, #F57C1F, #7DC9EA)' },
  previewChart: { flex: 1, padding: 16, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column' },
  previewChartTitle: { fontSize: 11, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },

  /* MARQUEE */
  marqueeSection: {
    padding: '30px 0 50px', position: 'relative', zIndex: 2,
    borderTop: '1px solid rgba(255,255,255,0.04)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    background: 'rgba(0,0,0,0.15)',
  },
  marqueeLabel: {
    textAlign: 'center', color: '#64748b', fontSize: 12.5,
    fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase',
    marginBottom: 22,
  },
  marqueeTrack: {
    overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, black 15%, black 85%, transparent)',
    WebkitMaskImage: 'linear-gradient(90deg, transparent, black 15%, black 85%, transparent)',
  },
  marqueeItem: {
    display: 'inline-block', padding: '0 36px',
    color: '#cbd5e1', fontSize: 22, fontWeight: 700,
    letterSpacing: 0.5, opacity: 0.55, whiteSpace: 'nowrap',
    fontFamily: '"SF Pro Display", system-ui, sans-serif',
  },

  /* LIGHT sections */
  lightSection: {
    padding: '110px 24px', position: 'relative', zIndex: 2,
    background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
    color: '#0f172a',
  },
  pricingSection: {
    padding: '110px 24px', position: 'relative', zIndex: 2,
    background: 'radial-gradient(ellipse at top, #e0f7fa 0%, #f8fafc 50%, #f1f5f9 100%)',
    color: '#0f172a',
  },
  statsSectionLight: {
    padding: '90px 24px', position: 'relative', zIndex: 2,
    background: '#ffffff',
    color: '#0f172a',
  },

  section: { padding: '100px 24px', position: 'relative', zIndex: 2 },
  sectionHead: { textAlign: 'center', maxWidth: 720, margin: '0 auto 56px' },
  sectionHeadLight: { textAlign: 'center', maxWidth: 720, margin: '0 auto 56px' },
  statsHead: { textAlign: 'center', maxWidth: 640, margin: '0 auto 48px' },

  eyebrow: {
    display: 'inline-flex', alignItems: 'center',
    padding: '5px 12px', borderRadius: 999,
    background: 'rgba(245, 124, 31, 0.12)', color: '#7DC9EA',
    fontSize: 11.5, fontWeight: 600, letterSpacing: 1.5,
    textTransform: 'uppercase', marginBottom: 18,
  },
  eyebrowLight: {
    display: 'inline-flex', alignItems: 'center',
    padding: '5px 12px', borderRadius: 999,
    background: 'rgba(245, 124, 31, 0.1)', color: '#DC6A0F',
    fontSize: 11.5, fontWeight: 600, letterSpacing: 1.5,
    textTransform: 'uppercase', marginBottom: 18,
  },
  sectionTitle: { fontSize: 'clamp(32px, 4.5vw, 56px)', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 20px' },
  sectionTitleLight: { fontSize: 'clamp(32px, 4.5vw, 56px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 20px' },
  sectionTitleSm: { fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 800, color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em', margin: '0 0 18px' },
  sectionSub: { fontSize: 'clamp(15px, 1.3vw, 17px)', color: '#94a3b8', lineHeight: 1.6, margin: 0 },
  sectionSubLight: { fontSize: 'clamp(15px, 1.3vw, 17px)', color: '#475569', lineHeight: 1.6, margin: 0 },

  /* BENTO */
  bentoGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 },
  bentoCard: {
    position: 'relative', overflow: 'hidden',
    padding: '28px 24px', borderRadius: 20,
    background: '#ffffff',
    border: '1px solid #e5e9f0',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    transition: 'transform 0.4s cubic-bezier(.2,.8,.2,1), box-shadow 0.3s, border-color 0.3s',
    minHeight: 220,
  },
  bentoIcon: {
    width: 44, height: 44, borderRadius: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid', marginBottom: 16,
  },
  bentoEyebrow: { fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 },
  bentoTitle: { fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
  bentoDesc: { fontSize: 14.5, color: '#475569', lineHeight: 1.55 },
  bentoGlow: { position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0, transition: 'opacity 0.3s' },

  /* STATS (light) */
  statsGridLight: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  statCellLight: {
    textAlign: 'left', padding: 28, borderRadius: 18,
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    border: '1px solid #e5e9f0',
    boxShadow: '0 4px 20px rgba(15,23,42,0.04)',
  },
  statIconLight: {
    width: 42, height: 42, borderRadius: 11,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 18,
  },
  statValueLight: {
    fontSize: 'clamp(30px, 3.5vw, 44px)', fontWeight: 800,
    color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1,
    marginBottom: 6,
  },
  statLabelLight: { fontSize: 13, color: '#64748b', fontWeight: 500 },

  /* SPOTLIGHT */
  spotlight: { display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 60, alignItems: 'center' },
  spotlightRight: { position: 'relative' },
  checkList: { listStyle: 'none', padding: 0, margin: '24px 0 32px', display: 'flex', flexDirection: 'column', gap: 12 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 10, color: '#cbd5e1', fontSize: 15 },
  aiCard: {
    background: 'rgba(255,255,255,0.95)', color: '#0f172a',
    borderRadius: 18, padding: 20,
    boxShadow: '0 40px 80px -20px rgba(245,124,31,0.35), 0 20px 50px -10px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  aiCardHead: { display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 14, borderBottom: '1px solid #f1f5f9', marginBottom: 14 },
  aiAvatar: { width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #F57C1F, #DC6A0F)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  aiMsgUser: { alignSelf: 'flex-end', marginLeft: 'auto', background: '#e0f7fa', color: '#006064', padding: '10px 14px', borderRadius: '14px 14px 4px 14px', fontSize: 13.5, fontWeight: 500, maxWidth: '85%', marginBottom: 10 },
  aiMsgBot: { background: '#f8fafc', color: '#334155', padding: '12px 14px', borderRadius: '14px 14px 14px 4px', fontSize: 13.5, lineHeight: 1.55, marginBottom: 14, border: '1px solid #e2e8f0' },
  aiChips: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 },
  aiChip: { padding: '5px 10px', borderRadius: 999, background: '#e0f7fa', color: '#DC6A0F', fontSize: 11.5, fontWeight: 600, cursor: 'default' },
  aiInput: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' },

  /* TABS */
  tabsWrap: { maxWidth: 960, margin: '0 auto' },
  tabsBar: {
    display: 'flex', gap: 6, padding: 6,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 14, marginBottom: 18, width: 'fit-content', marginLeft: 'auto', marginRight: 'auto',
  },
  tabBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 18px', borderRadius: 10,
    background: 'transparent', border: 'none',
    color: '#94a3b8', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.2s',
  },
  tabBtnActive: {
    background: 'linear-gradient(135deg, #F57C1F, #DC6A0F)', color: '#fff',
    boxShadow: '0 6px 18px rgba(245,124,31,0.35)',
  },
  tabsPanel: {
    borderRadius: 18, overflow: 'hidden',
    background: 'linear-gradient(180deg, #0f172a 0%, #111d3b 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 40px 100px -30px rgba(245,124,31,0.25), 0 20px 60px -20px rgba(0,0,0,0.5)',
    minHeight: 380,
  },
  tabsContent: { display: 'flex', flexDirection: 'column' },

  /* TESTIMONIALS */
  testimonialGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 },
  testimonialCard: {
    padding: 28, borderRadius: 18,
    background: '#ffffff', border: '1px solid #e5e9f0',
    boxShadow: '0 4px 20px rgba(15,23,42,0.04)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  },
  testimonialStars: { display: 'flex', gap: 2, marginBottom: 14 },
  testimonialQuote: {
    fontSize: 16, color: '#0f172a',
    lineHeight: 1.55, fontWeight: 500, marginBottom: 22,
  },
  testimonialMeta: { display: 'flex', alignItems: 'center', gap: 12 },
  testimonialAvatar: {
    width: 40, height: 40, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 700, fontSize: 15,
  },

  /* PRICING */
  pricingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'stretch' },
  planCard: {
    position: 'relative',
    padding: 32, borderRadius: 20,
    background: '#ffffff', border: '1px solid #e5e9f0',
    boxShadow: '0 4px 20px rgba(15,23,42,0.04)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    display: 'flex', flexDirection: 'column',
  },
  planCardHighlight: {
    background: 'linear-gradient(180deg, #ffffff 0%, #f0fdfa 100%)',
    border: '2px solid #F57C1F',
    boxShadow: '0 20px 50px -10px rgba(245,124,31,0.25)',
    transform: 'translateY(-6px)',
  },
  planBadge: {
    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
    padding: '5px 14px', borderRadius: 999,
    background: 'linear-gradient(135deg, #F57C1F, #DC6A0F)',
    color: '#fff', fontSize: 11, fontWeight: 700,
    letterSpacing: 1, textTransform: 'uppercase',
    boxShadow: '0 6px 16px rgba(245,124,31,0.4)',
  },
  planName: { fontSize: 14, fontWeight: 700, color: '#DC6A0F', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 },
  planPrice: { display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 12 },
  planPriceVal: { fontSize: 40, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' },
  planPriceP: { fontSize: 14, color: '#64748b' },
  planDesc: { fontSize: 14, color: '#475569', lineHeight: 1.55, marginBottom: 22, minHeight: 44 },
  planFeatures: { listStyle: 'none', padding: 0, margin: '22px 0 0', display: 'flex', flexDirection: 'column', gap: 10 },
  planFeature: { display: 'flex', alignItems: 'center', gap: 10, color: '#334155', fontSize: 14 },

  /* FAQ */
  faqWrap: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 18, padding: '4px 28px',
  },

  /* CTA */
  ctaSection: { padding: '60px 24px 120px', position: 'relative', zIndex: 2 },
  ctaCard: {
    position: 'relative', overflow: 'hidden',
    padding: '72px 40px', borderRadius: 24, textAlign: 'center',
    background: 'linear-gradient(180deg, rgba(245,124,31,0.08) 0%, rgba(245,124,31,0.04) 100%)',
    border: '1px solid rgba(245, 124, 31, 0.2)',
    backdropFilter: 'blur(10px)',
  },
  ctaGlow: { position: 'absolute', inset: 0, zIndex: 0, background: 'radial-gradient(circle at 50% 0%, rgba(245,124,31,0.25), transparent 60%)', pointerEvents: 'none' },
  ctaTitle: { position: 'relative', fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 16px' },
  ctaSub: { position: 'relative', fontSize: 16, color: '#94a3b8', maxWidth: 520, margin: '0 auto 32px', lineHeight: 1.6 },

  /* FOOTER */
  footer: { padding: '40px 24px', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.25)', position: 'relative', zIndex: 2 },
  footerInner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 },
  footerMeta: { fontSize: 13, color: '#64748b' },

  /* ─── FEATURE UNIVERSE SECTION ─── */
  universeSection: {
    padding: '120px 24px',
    position: 'relative',
    zIndex: 2,
    background:
      'radial-gradient(1200px 600px at 10% 0%, rgba(245,124,31,0.08), transparent 50%),' +
      'radial-gradient(1200px 600px at 90% 100%, rgba(41,168,224,0.08), transparent 50%)',
  },
  universeStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 1,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    overflow: 'hidden',
    marginBottom: 56,
  },
  universeStatCell: {
    background: 'rgba(10,15,30,0.55)',
    padding: '28px 20px',
    textAlign: 'center',
  },
  universeStatValue: {
    fontSize: 'clamp(30px, 4vw, 44px)',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #F57C1F, #29A8E0)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.02em',
    lineHeight: 1,
  },
  universeStatLabel: {
    marginTop: 8,
    fontSize: 12.5,
    color: '#94a3b8',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  universeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 20,
  },
  universeCard: {
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 18,
    padding: '24px 22px 18px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'border-color 0.3s, transform 0.3s, background 0.3s',
  },
  universeCardHead: {
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    marginBottom: 18,
  },
  universeIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  universeTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  },
  universeBlurb: {
    marginTop: 4,
    fontSize: 13,
    color: '#94a3b8',
    lineHeight: 1.5,
  },
  universeList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
  },
  universeItem: {
    display: 'flex',
    gap: 9,
    fontSize: 13,
    color: '#cbd5e1',
    lineHeight: 1.5,
    alignItems: 'flex-start',
  },
  universeFooter: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  universeFooterArrow: {
    color: '#64748b',
    fontSize: 16,
    transition: 'transform 0.2s',
  },
  universeCta: {
    marginTop: 56,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
  },
  universeCtaText: {
    fontSize: 15,
    color: '#94a3b8',
    maxWidth: 540,
    margin: 0,
    lineHeight: 1.6,
  },
};

/* ─────────────────────────── CSS ─────────────────────────── */

const css = `
@keyframes oz-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.4); }
}
@keyframes oz-fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes oz-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-14px); }
}
@keyframes oz-drift {
  0%   { transform: translate(0,0) scale(1); }
  33%  { transform: translate(40px, -30px) scale(1.1); }
  66%  { transform: translate(-30px, 40px) scale(0.95); }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes oz-word-rotate-in {
  0%   { opacity: 0; transform: translateY(28px) rotateX(-55deg); filter: blur(8px); }
  60%  { opacity: 1; filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) rotateX(0); filter: blur(0); }
}
@keyframes oz-caret {
  0%, 60% { opacity: 1; }
  61%, 100% { opacity: 0; }
}
@keyframes oz-marquee {
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
}
@keyframes oz-tab-fade {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

.oz-orb {
  position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.45;
  will-change: transform;
}
.oz-orb-1 { width: 520px; height: 520px; background: radial-gradient(circle, #F57C1F, transparent 70%); top: -120px; left: -100px; animation: oz-drift 18s ease-in-out infinite; }
.oz-orb-2 { width: 480px; height: 480px; background: radial-gradient(circle, #29A8E0, transparent 70%); top: 30%; right: -80px; animation: oz-drift 22s ease-in-out infinite reverse; }
.oz-orb-3 { width: 420px; height: 420px; background: radial-gradient(circle, #DC6A0F, transparent 70%); bottom: -120px; left: 30%; animation: oz-drift 26s ease-in-out infinite; }

.oz-grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
}

.oz-noise {
  position: absolute; inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>");
  opacity: 0.04; pointer-events: none; mix-blend-mode: overlay;
}

.oz-fade-up { opacity: 0; transform: translateY(24px); animation: oz-fadeUp 0.9s cubic-bezier(.2,.8,.2,1) forwards; }
.oz-delay-1 { animation-delay: 0.08s; }
.oz-delay-2 { animation-delay: 0.18s; }
.oz-delay-3 { animation-delay: 0.30s; }
.oz-delay-4 { animation-delay: 0.45s; }

.oz-reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.8s cubic-bezier(.2,.8,.2,1), transform 0.8s cubic-bezier(.2,.8,.2,1); }
.oz-reveal.oz-revealed { opacity: 1; transform: translateY(0); }

.oz-word-rotate { display: inline-block; transform-origin: 50% 100%; animation: oz-word-rotate-in 0.85s cubic-bezier(.2,.8,.2,1) both; will-change: transform, opacity, filter; }

.oz-caret {
  display: inline-block; width: 3px; height: 0.85em;
  background: linear-gradient(180deg, #7DC9EA, #F57C1F);
  margin-left: 4px; vertical-align: middle;
  border-radius: 2px;
  animation: oz-caret 1s steps(1) infinite;
  box-shadow: 0 0 12px #F57C1F;
}

.oz-marquee {
  display: inline-flex;
  animation: oz-marquee 40s linear infinite;
  will-change: transform;
}

.oz-tab-fade { animation: oz-tab-fade 0.45s cubic-bezier(.2,.8,.2,1) both; }

.oz-bento-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 60px -20px rgba(15,23,42,0.15);
  border-color: #cbd5e1 !important;
}
.oz-bento-card:hover > div:last-child { opacity: 1; }

.oz-btn-primary:hover, .oz-btn-primary-lg:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(245,124,31,0.55) !important;
}
.oz-btn-ghost:hover, .oz-btn-ghost-lg:hover {
  background: rgba(255,255,255,0.08) !important;
  border-color: rgba(255,255,255,0.25) !important;
}

.oz-float { animation: oz-float 6s ease-in-out infinite; }
.oz-nav-links a:hover { color: #fff !important; }

/* Universe card hover — lifts + tints border to the category colour */
.oz-universe-card {
  --tint: #F57C1F;
}
.oz-universe-card:hover {
  border-color: var(--tint) !important;
  transform: translateY(-4px);
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03)) !important;
  box-shadow: 0 18px 48px -24px var(--tint);
}
.oz-universe-card:hover .oz-universe-footer-arrow,
.oz-universe-card:hover span[style*="color"] { }

/* Responsive */
/* ═════════════ RESPONSIVE BREAKPOINTS ═════════════ */
/* className-based so it survives React inline-style serialization. */

/* Universal mobile safeguard — no horizontal scroll, ever. */
html, body { overflow-x: hidden; max-width: 100%; }
img, svg, video, canvas { max-width: 100%; height: auto; }

/* ─── Tablet & below (< 960px) ────────────────────────────────── */
@media (max-width: 960px) {
  .oz-nav-links { display: none !important; }

  /* Hero heading + sub tighten */
  section h1 { font-size: clamp(36px, 6vw, 56px) !important; line-height: 1.08 !important; }
  section h2 { font-size: clamp(28px, 5vw, 44px) !important; line-height: 1.12 !important; }

  /* Big side-by-side layouts (AI spotlight, hero) collapse to stacked */
  .oz-spotlight,
  div[style*="grid-template-columns: 1.05fr 1fr"],
  div[style*="grid-template-columns: 1fr 1fr"] {
    grid-template-columns: 1fr !important;
    gap: 32px !important;
  }
}

/* ─── Phones (< 760px) ────────────────────────────────────────── */
@media (max-width: 760px) {
  section { padding-top: 72px !important; padding-bottom: 72px !important; }

  /* Universe grid — 1 column */
  .oz-universe-grid { grid-template-columns: 1fr !important; }

  /* Universe stat strip 4 → 2 */
  .oz-universe-stats { grid-template-columns: repeat(2, 1fr) !important; }

  /* Bento / stats / testimonial / pricing grids — 1 column */
  .oz-bento-grid { grid-template-columns: 1fr !important; }
  .oz-bento-grid > * { grid-column: span 1 !important; }
  .oz-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
  .oz-testimonial-grid { grid-template-columns: 1fr !important; }
  .oz-pricing-grid { grid-template-columns: 1fr !important; }

  /* Nav logo shrinks */
  nav svg[aria-label="Oralzy"] { height: 26px !important; }

  /* Hero preview card: flatten 3D transform so it doesn't clip */
  .oz-hero-preview { transform: none !important; overflow-x: auto !important; }
}

/* ─── Small phones (< 520px) ──────────────────────────────────── */
@media (max-width: 520px) {
  section { padding: 56px 16px !important; }

  /* Universe cards compact */
  .oz-universe-card { padding: 20px 18px 16px !important; }
  .oz-universe-card li { font-size: 12.5px !important; }
  .oz-universe-stat-value { font-size: 28px !important; }

  /* Hero text sizes — avoid overflow */
  section h1 { font-size: clamp(26px, 9vw, 38px) !important; line-height: 1.1 !important; }
  section h2 { font-size: clamp(22px, 7vw, 32px) !important; line-height: 1.15 !important; }
  section p  { font-size: 14.5px !important; }

  /* CTA buttons fill full width so thumbs reach them */
  button.oz-btn-primary-lg,
  button.oz-btn-ghost-lg {
    width: 100% !important;
    justify-content: center !important;
  }

  /* Nav compact */
  nav button { padding: 8px 12px !important; font-size: 13px !important; }
  nav { padding: 10px 14px !important; }

  /* Marquee speeds up */
  .oz-marquee { animation-duration: 28s !important; }
}

/* ─── Tiny phones (< 380px — iPhone SE 1st gen etc.) ──────────── */
@media (max-width: 380px) {
  section { padding: 48px 14px !important; }
  section h1 { font-size: 26px !important; }
  section h2 { font-size: 22px !important; }
  nav svg[aria-label="Oralzy"] { height: 22px !important; }
  nav button { padding: 6px 10px !important; font-size: 12px !important; }

  /* Universe stat strip single column on really small screens */
  .oz-universe-stats { grid-template-columns: 1fr !important; }
  .oz-universe-stat-value { font-size: 24px !important; }
}

/* ─── Respect users who ask for less motion ───────────────────── */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .oz-orb, .oz-float, .oz-marquee { animation: none !important; }
}
`;

export default LandingPage;
